import Immutable from 'immutable';
import { createSelector } from '@reduxjs/toolkit';

import { colord } from 'colord';
import type { BrandKit, BrandKitColor } from 'js/src/client';
import StoreORM from 'js/src/data/store-orm';
import type { ImmutableMap } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';

export function isLoading(state: RootState) {
    return state.brandKits.loading;
}

export function getTeamBrandKitsCount(state: RootState) {
    return state.brandKits.teamCount;
}

export function isBrandKitSelected(state: RootState) {
    return state.brandKits.selectedBrandKitId !== null;
}

export function getBrandKitList(state: RootState) {
    return state.brandKits.list;
}

export const getAllBrandKits = createSelector(
    (state: RootState) => state.brandKits?.list,
    (brandKits): Immutable.List<ImmutableMap<BrandKit>> => {
        if (!brandKits) {
            return Immutable.List();
        }
        const orm = new StoreORM<BrandKit>(brandKits);
        return orm.map((brandKit) => brandKit);
    },
);

export function getBrandKitIdList(state: RootState) {
    const list = state.brandKits?.list;
    if (!list) {
        return Immutable.List<number>();
    }
    return new StoreORM<BrandKit>(list).getAllIds();
}

export function getBrandKitById(state: RootState, brandKitId: number) {
    const list = state.brandKits?.list;
    if (!list) {
        return undefined;
    }
    return new StoreORM<BrandKit>(list).getByIdOrUndefined(brandKitId);
}

export function getBrandKitColorsById(
    state: RootState,
    brandKitId: number,
): Immutable.List<ImmutableMap<BrandKitColor>> {
    const brandKitORM = getBrandKitById(state, brandKitId);
    if (brandKitORM) {
        return brandKitORM.get('colors');
    }
    return Immutable.List();
}

export const getBrandKitColorsInHex = createSelector(
    [(state, brandKitId) => getBrandKitColorsById(state, brandKitId)],
    (brandKitColors) => {
        return brandKitColors.map((color) => {
            const rgb = `rgb(${color.get('r')}, ${color.get('g')}, ${color.get('b')})`;
            return colord(rgb).toHex();
        });
    },
);
